import React, { useState, useEffect } from "react";
import Modal from "@mui/material/Modal";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { cambioDeestadoUsuario, editarUsuario, recuperarContraseña, ValidarUsuario } from "../../../store/admin/thunkAdmins";

const ModalEditarAdmin = ({ open, handleClose, functions, datos }) => {
    const dispatch = useDispatch();

    // Correo inicial para validar cambios
    const initialEmail = datos?.email_admin || "";

    // Estado del formulario, inicializado con datos proporcionados o valores por defecto
    const [formData, setFormData] = useState({
        name_admin: "",
        email_admin: "",
        phone_admin: "0000",
        cedula_admin: "0000",
        role: "Usuario",
        puesto: "Ventas",
        area: "Mercadeo y ventas",
        firma: "--",
    });

    const [errors, setErrors] = useState({});
    const [emailExists, setEmailExists] = useState(false);

    // Actualizar estado con datos proporcionados
    useEffect(() => {
        if (datos) {
            setFormData({
                name_admin: datos.name_admin || "",
                email_admin: datos.email_admin || "",
                phone_admin: datos.phone_admin || "0000",
                cedula_admin: datos.cedula_admin || "0000",
                role: datos.role || "Usuario",
                puesto: datos.puesto || "Ventas",
                area: datos.area || "Mercadeo y ventas",
                firma: datos.firma || "--",
                id_admin: datos.id_admin,
            });
        }
    }, [datos]);

    // Manejar cambios en los inputs
    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value ?? "",
        }));

        // Validar si el campo está vacío
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: String(value ?? "").trim() === "",
        }));

        // Validar email si es diferente al inicial
        if (name === "email_admin" && value !== initialEmail) {
            await validarEmail(value);
        } else {
            setEmailExists(false);
        }
    };

    // Validar el correo electrónico
    const validarEmail = async (email) => {
        if (String(email ?? "").trim() === "") {
            setEmailExists(false);
            return;
        }

        try {
            const exists = await dispatch(ValidarUsuario(email));
            setEmailExists(exists["0"].count === 1);
        } catch (error) {
            console.error("Error al validar el correo:", error);
        }
    };

    // Validar formulario
    const validateForm = () => {
        const newErrors = {};
        Object.keys(formData).forEach((key) => {
            // Convertir a string para evitar errores con trim()
            newErrors[key] = String(formData[key] ?? "").trim() === "";
        });
        setErrors(newErrors);
        return !Object.values(newErrors).includes(true);
    };

    // Manejar envío del formulario
    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            console.error("Hay campos vacíos");
            return;
        }

        if (emailExists) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "El correo electrónico ya está en uso. Por favor, utiliza otro correo.",
                confirmButtonText: "Aceptar",
            });
            return;
        }

        handleClose();

        const result = await Swal.fire({
            title: "¿Estás seguro?",
            text: "Estás a punto de editar este usuario.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Sí, editar usuario",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            Swal.fire({
                title: "Editando Usuario",
                text: "Por favor espere...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            try {
                // Lógica para editar usuario
                await dispatch(editarUsuario(formData));

                Swal.fire({
                    icon: "success",
                    title: "Usuario editado",
                    text: "El usuario ha sido editado exitosamente.",
                    showConfirmButton: false,
                    timer: 1000,
                });

                setTimeout(() => {
                    setFormData(datos);
                    functions();
                }, 1000);
            } catch (error) {
                console.error(error);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Hubo un problema al editar el usuario. Por favor, inténtelo de nuevo.",
                    confirmButtonText: "Aceptar",
                });
            }
        }
    };

    // Obtener la clase del input
    const getInputClass = (fieldName) => {
        return errors[fieldName] ? "form-control is-invalid" : "form-control";
    };

    const solicitudCambioClave = async () => {
        handleClose();
        const result = await Swal.fire({
            title: "¿Estás seguro?",
            text: "Estás a punto de solicitar una nueva clave de acceso para este usuario.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Sí, solicitar nueva clave",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            Swal.fire({
                title: "Solicitando Cambio de Clave",
                text: "Por favor espere...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            try {
                // Lógica para solicitar el cambio de clave
                await dispatch(recuperarContraseña(formData.email_admin));

                Swal.fire({
                    icon: "success",
                    title: "Solicitud Exitosa",
                    text: "Se ha solicitado una nueva clave de acceso exitosamente.",
                    showConfirmButton: false,
                    timer: 1000,
                });

                setTimeout(() => {
                    setFormData(datos);
                    functions();
                }, 1000);
            } catch (error) {
                console.error(error);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Hubo un problema al solicitar la nueva clave de acceso. Por favor, inténtelo de nuevo.",
                    confirmButtonText: "Aceptar",
                });
            }
        }
    };

    const cambioDeestado = async (estado) => {
        // Cierra cualquier modal o ventana emergente abierta
        handleClose();

        // Muestra una alerta de confirmación para el usuario
        const result = await Swal.fire({
            title: "¿Estás seguro?",
            text: estado === 1 ? "Estás a punto de conceder acceso a este usuario." : "Estás a punto de revocar el acceso de este usuario.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: estado === 1 ? "Sí, conceder acceso" : "Sí, revocar acceso",
            cancelButtonText: "Cancelar",
        });

        if (result.isConfirmed) {
            // Muestra una alerta de carga mientras se procesa la solicitud
            Swal.fire({
                title: estado === 1 ? "Concediendo acceso" : "Revocando acceso",
                text: "Por favor espere...",
                allowOutsideClick: false,
                allowEscapeKey: false,
                didOpen: () => {
                    Swal.showLoading();
                },
            });

            try {
                // Realiza la solicitud para cambiar el estado del usuario
                await dispatch(cambioDeestadoUsuario(datos.id_admin, estado));

                // Muestra un mensaje de éxito
                Swal.fire({
                    icon: "success",
                    title: "Operación Exitosa",
                    text: estado === 1 ? "El acceso ha sido concedido exitosamente." : "El acceso ha sido revocado exitosamente.",
                    showConfirmButton: false,
                    timer: 1000,
                });

                // Actualiza los datos del formulario después de un breve retraso
                setTimeout(() => {
                    setFormData(datos);
                    functions(); // Ejecuta cualquier función adicional necesaria
                }, 1000);
            } catch (error) {
                console.error(error); // Registra el error en la consola para depuración

                // Muestra un mensaje de error en caso de falla
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Hubo un problema al procesar la solicitud. Por favor, inténtelo de nuevo.",
                    confirmButtonText: "Aceptar",
                });
            }
        }
    };

    return (
        <Modal open={open} onClose={handleClose} className="modal fade show" style={{ display: "block" }}>
            <div className="modal-dialog" style={{ maxWidth: "60%", margin: "1.75rem auto" }}>
                <div className="modal-content">
                    <div className="modal-body">
                        <h5>Editar usuario administrador</h5>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "10px" }}>
                            <button className="btn btn-warning" onClick={solicitudCambioClave}>
                                Enviar nueva contraseña
                            </button>
                            {datos.status_admin === 1 ? (
                                <button className="btn btn-danger" onClick={() => cambioDeestado(0)}>
                                    Quitar Acceso
                                </button>
                            ) : (
                                <button className="btn btn-success" onClick={() => cambioDeestado(1)}>
                                    Conceder acceso
                                </button>
                            )}
                        </div>
                        <hr />

                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-lg-6">
                                    <div className="mb-3">
                                        <label>Nombre</label>
                                        <input type="text" name="id_admin" className={getInputClass("name_admin")} value={formData.id_admin || ""} onChange={handleInputChange} hidden />
                                        <input type="text" name="name_admin" className={getInputClass("name_admin")} value={formData.name_admin || ""} onChange={handleInputChange} />
                                    </div>

                                    <div className="mb-3">
                                        <label>Email</label>
                                        <input type="email" name="email_admin" className={getInputClass("email_admin")} value={formData.email_admin || ""} onChange={handleInputChange} />
                                        {emailExists && <div className="text-danger">Este correo ya existe.</div>}
                                    </div>

                                    <div className="mb-3">
                                        <label>Teléfono</label>
                                        <input type="text" name="phone_admin" className={getInputClass("phone_admin")} value={formData.phone_admin || ""} onChange={handleInputChange} />
                                    </div>

                                    <div className="mb-3">
                                        <label>Cédula</label>
                                        <input type="text" name="cedula_admin" className={getInputClass("cedula_admin")} value={formData.cedula_admin || ""} onChange={handleInputChange} />
                                    </div>
                                </div>

                                <div className="col-lg-6">
                                    <div className="mb-3">
                                        <label>Puesto</label>
                                        <input type="text" name="puesto" className={getInputClass("puesto")} value={formData.puesto || ""} onChange={handleInputChange} />
                                    </div>

                                    <div className="mb-3">
                                        <label>Área</label>
                                        <input type="text" name="area" className={getInputClass("area")} value={formData.area || ""} onChange={handleInputChange} />
                                    </div>

                                    <div className="mb-3">
                                        <label>Firma</label>
                                        <input type="text" name="firma" className={getInputClass("firma")} value={formData.firma || ""} onChange={handleInputChange} />
                                    </div>
                                </div>
                            </div>
                            <br />
                            <button type="submit" className="btn btn-dark">
                                Editar usuario
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default ModalEditarAdmin;
